import type { ReactNode } from "react";
import { Focusable } from "react-aria-components";
import { useWatch } from "react-hook-form";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import type { useFilters } from "@/providers/filter-provider";
import type { FiltersFormValues } from "@/providers/filter-registry";
import { cx } from "@/utils/cx";

type UnavailableOptionProps = {
  reason: string;
  className?: string;
  semantics?: "button" | "group" | "inherit";
  children: ReactNode;
};

export const UnavailableOption = ({ reason, className, semantics = "button", children }: UnavailableOptionProps) => (
  <Tooltip title={reason} placement="top" arrow>
    <Focusable>
      <span
        role={semantics === "inherit" ? undefined : semantics}
        tabIndex={0}
        aria-disabled="true"
        className={cx("cursor-not-allowed outline-hidden", className)}
      >
        {children}
      </span>
    </Focusable>
  </Tooltip>
);

type UnavailableRangeProps = {
  form: ReturnType<typeof useFilters>["form"];
  fields: (keyof FiltersFormValues)[];
  reason?: string;
  children: ReactNode;
};

export const UnavailableRange = ({ form, fields, reason, children }: UnavailableRangeProps) => {
  const values = useWatch({ control: form.control, name: fields });
  const hasValue = values.some((value) => value !== undefined && value !== null && value !== "");

  if (!reason || hasValue) return children;

  return (
    <UnavailableOption reason={reason} semantics="group" className="block w-full">
      <div inert className="opacity-50">
        {children}
      </div>
    </UnavailableOption>
  );
};
